/*eslint-disable*/
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { editUserPosition, getGlobalViewMachines, getGlobalViewUsers } from 'features/globalView/globalViewSlice'
import { getCenter } from 'pages/Zones/zoneConstants'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { GlobalViewMapContainer } from './GlobalView.styled'
import { IGLSocketResponse } from 'types/global-view.interface'
import { useWebSocket } from 'hooks/useWebSocket'
import { GreenPin } from 'assets/icons/GreenPin'
import { WorkerPinIcon } from 'assets/icons/WorkerPinIcon'
import { MultipleWorkersPinIcon } from 'assets/icons/MultipleWorkersPinicon'
import { MachinePinIcon } from 'assets/icons/MachinePinIcon'
import { OrangePin } from 'assets/icons/OrangePin'
import { getDashboardData } from 'features/dashboard/dashboardSlice'
import { WhitePin } from 'assets/icons/WhitePin'
import { PinFuelIcon } from 'assets/icons/PinFuelIcon'
import { BluePin } from 'assets/icons/BluePin'
import { RedPin } from 'assets/icons/RedPin'

export const GlobalViewMap = ({ map, setMap, filters, loadedPolygons, usersCluster, handleGetInfo, machinesCluster, handleSelectPin, usersTypeFilter, machinesTypeFilter }) => {
    const ref = useRef()
    const dispatch = useDispatch<AppDispatch>()
    const { users, areas, machines } = useSelector((state: RootState) => state.globalView)
    const userMarkers = useRef([])
    const machinesMarkers = useRef([])
    const [centerSet, setCenterSet] = useState(false)
    const hasSubscribedRef = useRef(false)

    const getMachinePin = (status: number) => {
        switch (status) {
            case 1:
                return GreenPin
            case 2:
                return OrangePin
            case 3:
                return RedPin
            default:
                return BluePin

        }
    }

    const handleWebSocketMessage = (event: MessageEvent) => {
        try {
            const data: IGLSocketResponse = JSON.parse(event.data)
            if (data.type === 'devicePositionUpdate') {
                dispatch(editUserPosition({
                    id: data.payload.DeviceId,
                    position: {
                        longitude: data.payload.Position[0],
                        latitude: data.payload.Position[1]
                    }
                }))
            }
        } catch (error) {
            console.error('Error handling WebSocket message:', error)
        }
    }

    const { sendMessage, wsState, WebSocketStates } = useWebSocket({
        onMessage: handleWebSocketMessage
    })

    // Subscribe to device positions
    useEffect(() => {
        const deviceIds = users.filter(u => u.position).map(u => 'user-' + u.id)
        if (!deviceIds.length) {
            hasSubscribedRef.current = false
            return
        }
        if (wsState === WebSocketStates.OPEN && !hasSubscribedRef.current) {
            sendMessage({
                action: 'subscribeToDevicePositions',
                deviceIds
            })
            hasSubscribedRef.current = true
        }
    }, [users, wsState])

    const mapOptions = {
        gestureHandling: 'greedy',
        zoom: 5,
        mapId: '4504f8b37365c3d0',
        center: {
            lat: 0,
            lng: 0,
        },
        disableDefaultUI: true,
        mapTypeId: 'satellite' as google.maps.MapTypeId,
        styles: [
            {
                featureType: 'all',
                elementType: 'labels',
                stylers: [
                    { visibility: 'off' }
                ]
            }
        ]
    }

    const markerStyle = ({ position, count }, machines?: boolean, fuel?: boolean) => {
        const content = document.createElement('div')
        content.className = 'marker-cluster'
        const pin = document.createElement('svg')
        pin.innerHTML = fuel ? WhitePin : machines ? OrangePin : GreenPin
        const container = document.createElement('div')
        container.className = 'marker-cluster-items'
        const icon = document.createElement('svg')
        icon.innerHTML = fuel ? PinFuelIcon : MultipleWorkersPinIcon
        const text = document.createElement('p')
        text.innerText = count
        container.appendChild(icon)
        container.appendChild(text)
        content.appendChild(pin)
        content.appendChild(container)

        return new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            content,
        })
    }

    useEffect(() => {
        if (map && !centerSet) {
            const pos = users?.find(u => u.position)
            if (users?.length > 0 && pos && filters.users) {
                map.setZoom(20)
                map.setCenter({ lat: pos.position.latitude, lng: pos.position.longitude });
                setCenterSet(true)
            } else if (areas.length > 0 && filters.areas) {
                const center = getCenter(areas[0])
                map.setZoom(20)
                map.setCenter(center)
                setCenterSet(true)
            }
        }
    }, [map, users, areas])


    useEffect(() => { // get machines and employees from backend
        let isCancelled = false;

        const pollMachines = async () => {
            await dispatch(getGlobalViewMachines({ status: machinesTypeFilter, initialLoading: machines.length < 0 }));

            if (!isCancelled) {
                setTimeout(pollMachines, 15000);
            }
        };

        pollMachines();



        dispatch(getGlobalViewUsers({ status: usersTypeFilter, initialLoading: !!users.length }));

        return () => {
            isCancelled = true;
        };
    }, [usersTypeFilter, machinesTypeFilter, machinesCluster]);



    useEffect(() => { // draw polygons
        loadedPolygons.current?.forEach(lp => {
            lp.polygon.setMap(null)
            lp.label.setMap(null)
        })
        loadedPolygons.current = []
        if (filters.areas && map) {
            loadedPolygons.current = areas.map((a) => {
                const center = getCenter(a)
                const content = document.createElement('p')
                content.innerText = a.areaName
                content.className = 'white-text'

                const polygon = new google.maps.Polygon({
                    paths: a.path,
                    fillColor: '#fff',
                    map,
                    strokeWeight: 0.5,
                    clickable: true,
                })

                polygon.addListener('click', () => handleGetInfo({ path: a.path, areaName: a.areaName, areaSize: a.areaSize, id: a.id }))

                return {
                    id: a.id,
                    label: new google.maps.marker.AdvancedMarkerElement({
                        position: { lat: Number(center.lat), lng: Number(center.lng) },
                        map,
                        content,
                    }),
                    polygon,
                }
            })
        }
    }, [areas, map, filters.areas])

    useEffect(() => {
        setMap(new google.maps.Map(ref.current, mapOptions))
        dispatch(getDashboardData())
    }, [])

    useEffect(() => { // draw employees pins
        if (map && filters.users) {
            usersCluster.current?.setMap(null)
            usersCluster.current = null
            userMarkers.current.map(m => m.setMap(null))
            userMarkers.current = []
            const customMarker = document.createElement('div')
            customMarker.className = 'test'
            userMarkers.current = users
                ?.filter((u) => u.position)
                .map(({ name, position, id }) => {
                    const content = document.createElement('div')
                    content.className = 'marker-cluster'
                    const pin = document.createElement('svg')
                    pin.innerHTML = GreenPin
                    const container = document.createElement('div')
                    container.className = 'marker-cluster-items alone-icon'
                    const icon = document.createElement('svg')
                    icon.innerHTML = WorkerPinIcon
                    const text = document.createElement('p')
                    text.className = 'pin-name'
                    text.innerText = name
                    container.appendChild(icon)
                    container.appendChild(text)
                    content.appendChild(pin)
                    content.appendChild(container)

                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        position: { lat: Number(position.latitude), lng: Number(position.longitude) },
                        map,
                        content,
                    })

                    marker.addListener('gmp-click', () => {
                        handleSelectPin && handleSelectPin({
                            id,
                            name,
                            type: 'employee',
                            opened: true
                        })
                    })
                    return marker
                })

            usersCluster.current = new MarkerClusterer({
                markers: userMarkers.current,
                map,
                algorithm: new SuperClusterAlgorithm({ radius: 200 }),
                renderer: ({
                    render: ({ count, position }) => markerStyle({ count, position })
                })
            })
        }
    }, [users, map])

    useEffect(() => { // draw employees pins
        if (map) {
            if (filters.machines) {
                // machinesMarkers.current.map(m => !machines.some(ma => ma.vehicle?.latitude == machinesMarkers.current[0]?.position.lat && ma.vehicle?.longitude == machinesMarkers.current[0]?.position.lng) && m.setMap(null))
                // machinesMarkers.current = machinesMarkers.current.filter(m => machines.some(ma => ma.vehicle?.latitude == m.position.lat && ma.vehicle?.longitude == m.position.lng))
                machinesCluster.current?.setMap(null)
                machinesCluster.current = null
                machinesMarkers.current.map(m => m.setMap(null))
                machinesMarkers.current = []
                const customMarker = document.createElement('div')
                customMarker.className = 'test'
                machinesMarkers.current = machines
                    .filter((m) => m.vehicle?.latitude)
                    .map(({ name, vehicle, id, status }) => {
                        const content = document.createElement('div')
                        content.className = 'marker-cluster'
                        const pin = document.createElement('svg')
                        pin.innerHTML = getMachinePin(status)
                        const container = document.createElement('div')
                        container.className = 'marker-cluster-items alone-icon'
                        const icon = document.createElement('svg')
                        icon.innerHTML = MachinePinIcon
                        const text = document.createElement('p')
                        text.className = 'pin-name'
                        text.innerText = name
                        container.appendChild(icon)
                        container.appendChild(text)
                        content.appendChild(pin)
                        content.appendChild(container)

                        const marker = new google.maps.marker.AdvancedMarkerElement({
                            position: { lat: Number(vehicle.latitude), lng: Number(vehicle.longitude) },
                            map,
                            content,
                        })

                        marker.addListener('gmp-click', () => {
                            handleSelectPin && handleSelectPin({
                                id,
                                name,
                                type: 'machine',
                                opened: true
                            })
                        })
                        return marker
                    })

                machinesCluster.current = new MarkerClusterer({
                    markers: machinesMarkers.current,
                    map,
                    algorithm: new SuperClusterAlgorithm({ radius: 200 }),
                    renderer: ({
                        render: ({ count, position }) => markerStyle({ count, position }, true)
                    })
                })
            }
        }
    }, [map, machines])

    return <GlobalViewMapContainer ref={ref} id='map' />
}
